import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  CreateCampaignTaskRequest,
  TaskApiRecord,
  UpdateTaskResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntity } from "./responseMappers";

const buildUpdateTaskUrl = (taskId: string): string =>
  API_ROUTES.UPDATE_TASK.replace("{task_id}", taskId);

/** Variables fed to `useUpdateCampaignTask.mutateAsync`. `campaignId` is
 *  optional and used purely to scope the cache invalidation to this campaign's
 *  detail query on success.
 *
 *  The body is the full create-shaped campaign task payload (incl.
 *  `campaign_id` / `challenge_id`): the BE task-update handler routes on
 *  `campaign_id`, and omitting it makes it fall back to a season lookup and
 *  404 ("Season None not found"). */
export type UpdateCampaignTaskVariables = {
  taskId: string;
  campaignId?: string | null;
  body: CreateCampaignTaskRequest;
};

const putUpdateCampaignTask = async ({
  taskId,
  body,
}: UpdateCampaignTaskVariables): Promise<TaskApiRecord> => {
  const response = await getPrivateInstance().put<UpdateTaskResponse>(
    buildUpdateTaskUrl(taskId),
    body,
  );
  return unwrapGamePassEntity(response.data, "task");
};

/**
 * Update a monthly challenge's tiered task
 * (`PUT /gamepass/v2/admin/tasks/{task_id}`). Campaigns reuse the season task
 * endpoint (see `ApiRoutes.js`); the body carries the monthly task fields +
 * `tiers` via {@link UpdateCampaignTaskRequest}.
 *
 * On success it invalidates the campaign's challenge + task lists (so the
 * Monthly Details page re-hydrates on the next read) and — when the caller
 * knows it — the single-campaign detail query.
 */
export const useUpdateCampaignTask = (): UseMutationResult<
  TaskApiRecord,
  Error,
  UpdateCampaignTaskVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "campaign-tasks", "update"],
    mutationFn: putUpdateCampaignTask,
    onSuccess: (_task, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "tasks", "list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "challenges", "list"],
      });
      if (variables.campaignId) {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "campaigns", "detail", variables.campaignId],
        });
      }
    },
  });
};

export { putUpdateCampaignTask };
